import FileDownloadIcon from '@mui/icons-material/FileDownload';
import {
  Alert,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import { hashCode } from '../common/miscFunctions';
import { ColumnDefinition } from '../common/table/ColumnDefinition';
import Table from '../common/table/Table';
import { ApiResponse } from '../types/ApiResponse';
import { LoadState } from '../types/LoadState';
import { getAsBlob } from './ForteckningarClient';
import { Forteckning } from './types/Forteckning';
import { Status } from './types/dtos/ForteckningDto';

interface ForteckningarListProps {
  savedForteckningar: Forteckning[];
  handleDelete: (forteckningar: Forteckning[]) => Promise<ApiResponse>;
  deleteLoadState: LoadState;
  handleRetry: (forteckning: Forteckning) => void;
  retryLoadState: LoadState;
}

interface ForteckningRow {
  id: number;
  forteckning: Forteckning;
}

export const ForteckningarList: React.FC<ForteckningarListProps> = ({
  savedForteckningar,
  handleDelete,
  handleRetry,
  deleteLoadState,
  retryLoadState,
}) => {
  const download = async (item: Forteckning) =>
    (await getAsBlob(item.id)).downloadBlob(
      getPrettyFilename(item.typ, item.registreradTidpunkt)
    );

  const columnHeaders: Record<'id' | 'header', string>[] = [
    {
      id: 'tidpunkt',
      header: 'Tidpunkt',
    },
    {
      id: 'information',
      header: 'Information',
    },
    {
      id: 'kundmarke',
      header: 'Kundmärke',
    },
  ];

  const columnDefinitions: ColumnDefinition<ForteckningRow>[] = [
    {
      id: 'registreradTidpunkt',
      header: 'Tidpunkt',
      render: (forteckningRow) => (
        <>{forteckningRow.forteckning.registreradTidpunkt}</>
      ),
      width: 200,
    },
    {
      id: 'typ',
      header: 'Förteckningstyp',
      width: 200,
      render: (forteckningRow) =>
        ({
          METAGF: <>Ägarförteckning</>,
          METAGRA: <>Ägare + rättigheter</>,
          METAGADR: <>Ägare + belägenhetsadress</>,
          METAGLATA: <>Ägarförteckning samtliga ägare</>,
        }[forteckningRow.forteckning.typ] || <></>),
    },
    {
      id: 'kundmarke',
      header: 'Kundmärke',
      render: (forteckningRow) => <>{forteckningRow.forteckning.kundmarke}</>,
      width: 200,
    },
    {
      id: 'links',
      header: '',
      render: (forteckningRow) => {
        const forteckning = forteckningRow.forteckning;
        const circularProgress = (
          <CircularProgress size="16px" sx={{ mt: -0.5, mb: -0.5 }} />
        );
        const renderStatus: Record<Status, JSX.Element> = {
          FINISHED: (
            <Button
              sx={{ mt: -0.5, mb: -0.5 }}
              onClick={() => download(forteckning)}
              size="small"
              endIcon={<FileDownloadIcon />}
            >
              Excel
            </Button>
          ),
          REGISTERED: circularProgress,
          PRODUCING: circularProgress,
          FAILED_RETRYABLE: (
            <Button
              variant="contained"
              onClick={() => handleRetry(forteckning)}
              disabled={retryLoadState === LoadState.Loading}
              sx={{ pt: 0.5, pb: 0.5 }}
            >
              Försök igen
            </Button>
          ),
          FAILED: <>Fel</>,
        };
        return renderStatus[forteckning.status];
      },
      width: 200,
    },
  ];

  return (
    <Stack spacing={2} sx={{ mt: 4, pl: 2, pr: 2 }}>
      <Typography variant="h6" color="primary">
        Sparade förteckningar
      </Typography>
      <Alert severity="info" sx={{ mt: 2 }}>
        Sparade förteckningar tas automatiskt bort efter 14 dagar
      </Alert>

      {savedForteckningar.find(
        (forteckning) => forteckning.status === 'FAILED_RETRYABLE'
      ) && (
        <Alert severity="error" sx={{ mt: 2 }}>
          En eller flera av dina förteckningar lyckades inte produceras. Du kan
          försöka igen eller ta bort den misslyckade förteckningen.
        </Alert>
      )}
      {deleteLoadState === LoadState.Error && (
        <Alert severity="error">
          Något gick fel när förteckning(ar) skulle tas bort. Försök igen.
        </Alert>
      )}
      <Table<ForteckningRow>
        data={savedForteckningar.map((forteckning) => ({
          id: hashCode(forteckning.id),
          forteckning,
        }))}
        rowsPerPage={6}
        columnHeaders={columnHeaders}
        columnDefinitions={columnDefinitions}
        deleteMulti={(forteckningRows) =>
          handleDelete(forteckningRows.map((row) => row.forteckning))
        }
        sortOrder="desc"
        sortColumn="tidpunkt"
      />
    </Stack>
  );
};
const getPrettyFilename = (typ: string, tidpunkt: string): string => {
  const trimmedTidpunkt = tidpunkt
    .replace(' ', '_')
    .replaceAll(/[^0-9a-z_åäö-]/g, '');
  return {
    METAGF: `Ägarförteckning_${trimmedTidpunkt}.xlsx`,
    METAGRA: `Ägare+rättigheter_${trimmedTidpunkt}.xlsx`,
    METAGADR: `Ägare+belägenhetsadress_${trimmedTidpunkt}.xlsx`,
    METAGLATA: `Ägarförteckning_samtliga_ägare_${trimmedTidpunkt}.xlsx`,
  }[typ];
};
